import { getVideoSourceUrl } from "src/components/MediaPreview/utils/getPlayerPreview";
import { MediaType } from "src/constants/constants";
import { getSecureUrl } from "src/domains/media/mediaPreview";
import { appConfig } from "src/appConfig";
import {
  getFileOutputImageSet,
  getMediaType,
  secureAndResizeUrl,
} from "src/helpers/mediaHelper";
import { File } from "src/types.g";

export type FileViewerSource =
  | { kind: MediaType.IMAGE | MediaType.VIDEO | MediaType.AUDIO; src: string }
  | { kind: MediaType.DOCUMENT; pages: string[] };

const VIEWABLE_TYPES = [
  MediaType.IMAGE,
  MediaType.VIDEO,
  MediaType.AUDIO,
  MediaType.DOCUMENT,
];

export const isFileViewable = (mimetype?: string) =>
  VIEWABLE_TYPES.includes(getMediaType({ mimetype }));

const DOCUMENT_PAGE_SIZE = 1600;

/**
 * Page images of a processed document. Built from the output's storage keys on
 * the secure media host, as the media thumbnails are; legacy outputs that only
 * carry `urls` are used as stored, matching the media preview modal.
 */
const getDocumentPages = (
  file: Partial<File>,
  secureMediaPolicy: string | undefined,
): string[] => {
  const content = getFileOutputImageSet(file)?.content;
  const keys: string[] = content?.keys ?? [];
  if (keys.length) {
    return keys.map((key) =>
      secureAndResizeUrl({
        url: `${appConfig.secureMediaUrl}/${key}`,
        width: DOCUMENT_PAGE_SIZE,
        height: DOCUMENT_PAGE_SIZE,
        fit: "clip",
        secureMediaPolicy,
      }),
    );
  }
  return content?.urls ?? [];
};

/** Resolves what to render for a file; undefined when nothing can be shown. */
export const getFileViewerSource = (
  file: Partial<File>,
  secureMediaPolicy: string | undefined,
): FileViewerSource | undefined => {
  const secure = (url?: string | null) =>
    url ? getSecureUrl(url, secureMediaPolicy) : undefined;

  switch (getMediaType(file)) {
    case MediaType.IMAGE: {
      const src = secure(file.source);
      return src ? { kind: MediaType.IMAGE, src } : undefined;
    }
    case MediaType.VIDEO: {
      // Prefer the processed MP4; fall back to the upload while it processes.
      const src =
        getVideoSourceUrl(file, secureMediaPolicy) || secure(file.source);
      return src ? { kind: MediaType.VIDEO, src } : undefined;
    }
    case MediaType.AUDIO: {
      const src = secure(file.source);
      return src ? { kind: MediaType.AUDIO, src } : undefined;
    }
    case MediaType.DOCUMENT: {
      const pages = getDocumentPages(file, secureMediaPolicy);
      return pages.length ? { kind: MediaType.DOCUMENT, pages } : undefined;
    }
    default:
      return undefined;
  }
};
